import { useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Download, FileText, Image as ImageIcon, ExternalLink } from "lucide-react";
import architectureSvg from "@/assets/helpdesk-architecture-phase3.svg";

type ChecklistItem = { id: string; task: string; owner: string; deps: string; target: string };
type Phase = { code: string; title: string; window: string; items: ChecklistItem[] };

const PHASES: Phase[] = [
  {
    code: "3a", title: "Schema + RAG edge function", window: "T0 → T0+1w",
    items: [
      { id: "3a.1", task: "Migration: helpdesk_conversations / _messages / _message_sources / _rate_limits (+ RLS + GRANTs)", owner: "Backend", deps: "Phase 2 KB tables", target: "T0+2d" },
      { id: "3a.2", task: "SQL function match_kb_chunks() using pgvector <=>", owner: "Backend", deps: "3a.1", target: "T0+3d" },
      { id: "3a.3", task: "Edge function helpdesk-chat (auth, rate limit, retrieval, prompt, SSE)", owner: "Backend", deps: "3a.1, 3a.2", target: "T0+5d" },
      { id: "3a.4", task: "HELPDESK_TOKEN_SECRET provisioned + HMAC anon token helper", owner: "Backend", deps: "—", target: "T0+3d" },
      { id: "3a.5", task: "Feature flag + dogfood on /support", owner: "Frontend", deps: "3a.3", target: "T0+1w" },
    ],
  },
  {
    code: "3b", title: "Streaming UI + citations + feedback", window: "T0+1w → T0+2w",
    items: [
      { id: "3b.1", task: "SSE reader + token streaming in HelpdeskWidget", owner: "Frontend", deps: "3a.3", target: "T0+9d" },
      { id: "3b.2", task: "Source pill list + hover preview from kb_articles", owner: "Frontend", deps: "3b.1", target: "T0+10d" },
      { id: "3b.3", task: "helpdesk-feedback edge function + inline 👍/👎 UI", owner: "Full-stack", deps: "3a.1", target: "T0+11d" },
      { id: "3b.4", task: "Global FAB mount in App.tsx (excluded on auth routes)", owner: "Frontend", deps: "3b.1", target: "T0+12d" },
    ],
  },
  {
    code: "3c", title: "Escalation + ticket integration", window: "T0+2w → T0+3w",
    items: [
      { id: "3c.1", task: "Edge function helpdesk-escalate writing to support_tickets", owner: "Backend", deps: "3a.1", target: "T0+15d" },
      { id: "3c.2", task: "Escalation modal with pre-filled transcript", owner: "Frontend", deps: "3c.1", target: "T0+16d" },
      { id: "3c.3", task: "Email enqueue via process-email-queue (staff + user)", owner: "Backend", deps: "3c.1", target: "T0+17d" },
      { id: "3c.4", task: "Auto-escalation triggers (low confidence, 2× thumbs-down)", owner: "Backend", deps: "3a.3, 3b.3", target: "T0+19d" },
    ],
  },
  {
    code: "3d", title: "Admin dashboards + audit hooks", window: "T0+3w → T0+4w",
    items: [
      { id: "3d.1", task: "Admin → Support → Conversations table + drill-in", owner: "Frontend", deps: "3a.1", target: "T0+23d" },
      { id: "3d.2", task: "Quality metrics panel (deflection, latency, top unanswered)", owner: "Frontend", deps: "3b.3", target: "T0+25d" },
      { id: "3d.3", task: "Audit log-admin-action on conversation view + overrides", owner: "Backend", deps: "Phase 2 audit log", target: "T0+24d" },
      { id: "3d.4", task: "Edge-function structured JSON logging", owner: "Backend", deps: "3a.3", target: "T0+22d" },
    ],
  },
  {
    code: "3e", title: "Eval harness + safety regression", window: "T0+4w → T0+5w",
    items: [
      { id: "3e.1", task: "Gold Q/A set + retrieval recall scorer", owner: "QA", deps: "3a.2", target: "T0+29d" },
      { id: "3e.2", task: "Faithfulness scorer (LLM-as-judge) wired into nightly CI", owner: "QA", deps: "3e.1", target: "T0+31d" },
      { id: "3e.3", task: "Prompt-injection / PII redaction regression suite", owner: "Security", deps: "3a.3", target: "T0+33d" },
      { id: "3e.4", task: "Anonymous-conversation retention purge (scheduled function)", owner: "Backend", deps: "3a.1", target: "T0+34d" },
    ],
  },
];

const REQUEST_FLOW = [
  { step: "Auth + rate limit", detail: "Validate JWT via supabase.auth.getUser(); for anon, derive a stable fingerprint + IP bucket. Increment helpdesk_rate_limits (e.g., 20 msg / 5 min / identity)." },
  { step: "Conversation upsert", detail: "Create or load helpdesk_conversations; append the user helpdesk_messages row." },
  { step: "Retrieval", detail: "Embed query with google/gemini-embedding-001 (768d). Call match_kb_chunks(query_embedding, k=12, min_sim=0.62) using pgvector <=>. Optional MMR rerank; keep top 5." },
  { step: "Prompt assembly", detail: "System prompt enforces context-only answers, [#n] citations mapped to article slugs, and a structured escalate=true hint when confidence is low." },
  { step: "Generation", detail: "Stream from google/gemini-2.5-flash via Lovable AI Gateway (SSE → client). Persist final assistant message + helpdesk_message_sources + token/latency telemetry." },
  { step: "Safety pass", detail: "Regex + model-based moderation on outbound text; redact emails/keys; block PII echo." },
  { step: "Response", detail: "Stream tokens; on finish, emit a JSON trailer with message_id, sources, suggest_escalation." },
];

export default function HelpdeskArchitectureDocs() {
  const onDownloadPdf = useCallback(async () => {
    // Pre-rendered, formatted PDF served from /public so it stays shareable.
    const a = document.createElement("a");
    a.href = "/helpdesk-architecture-phase3.pdf";
    a.download = "helpdesk-architecture-phase3.pdf";
    document.body.appendChild(a); a.click(); a.remove();
  }, []);

  return (
    <main className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="container max-w-5xl py-8">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <span>/</span>
            <Link to="/support" className="hover:text-foreground">Support</Link>
            <span>/</span>
            <span className="text-foreground">Helpdesk architecture</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
            AI Helpdesk — Phase 3 Technical Architecture
          </h1>
          <p className="mt-3 text-muted-foreground max-w-2xl">
            How the Phase 1 widget and Phase 2 admin tooling become a production-grade,
            RAG-powered support system with escalation, observability, and safety controls.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <Button onClick={onDownloadPdf}>
              <Download className="mr-2 h-4 w-4" /> Download Phase 3 Architecture PDF
            </Button>
            <Button variant="outline" asChild>
              <a href="/helpdesk-architecture-phase3.svg" download>
                <FileText className="mr-2 h-4 w-4" /> Editable SVG
              </a>
            </Button>
            <Button variant="outline" asChild>
              <a href="/helpdesk-architecture-phase3.png" download>
                <ImageIcon className="mr-2 h-4 w-4" /> PNG (2000px)
              </a>
            </Button>
            <Button variant="ghost" asChild>
              <a href="https://github.com/" target="_blank" rel="noreferrer">
                <ExternalLink className="mr-2 h-4 w-4" /> Source markdown
              </a>
            </Button>
          </div>
        </div>
      </header>

      <section className="container max-w-5xl py-10 space-y-10">
        <Card>
          <CardHeader>
            <CardTitle>1. High-level architecture</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <img
                src={architectureSvg}
                alt="Phase 3 architecture: React widget → edge functions (helpdesk-chat, escalate, feedback, kb-ingest, log-admin-action) → Postgres + pgvector and Lovable AI Gateway."
                className="w-full h-auto"
              />
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              Source SVG lives at <code>src/assets/helpdesk-architecture-phase3.svg</code> — edit it directly to update the diagram.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>2. Request flow — helpdesk-chat</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-4">
              {REQUEST_FLOW.map((s, i) => (
                <li key={s.step} className="flex gap-4">
                  <div className="flex-shrink-0 h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold text-sm">
                    {i + 1}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-foreground">{s.step}</div>
                    <p className="text-sm text-muted-foreground mt-1">{s.detail}</p>
                  </div>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>3. Phase 3 implementation checklist</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Owners are role labels — assign concrete names in the project tracker. Dates are target completion (T0 = Phase 3 kick-off).
            </p>
          </CardHeader>
          <CardContent className="space-y-8">
            {PHASES.map((p) => (
              <div key={p.code}>
                <div className="flex items-baseline gap-3 mb-3">
                  <Badge variant="secondary" className="font-mono">{p.code}</Badge>
                  <h3 className="text-lg font-semibold">{p.title}</h3>
                  <span className="text-sm text-muted-foreground">{p.window}</span>
                </div>
                <div className="overflow-x-auto rounded-md border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-left">
                      <tr>
                        <th className="px-3 py-2 font-medium w-16">#</th>
                        <th className="px-3 py-2 font-medium">Task</th>
                        <th className="px-3 py-2 font-medium w-28">Owner</th>
                        <th className="px-3 py-2 font-medium w-40">Depends on</th>
                        <th className="px-3 py-2 font-medium w-24">Target</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.items.map((it) => (
                        <tr key={it.id} className="border-t border-border">
                          <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{it.id}</td>
                          <td className="px-3 py-2">{it.task}</td>
                          <td className="px-3 py-2"><Badge variant="outline">{it.owner}</Badge></td>
                          <td className="px-3 py-2 text-muted-foreground">{it.deps}</td>
                          <td className="px-3 py-2 font-mono text-xs">{it.target}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Editable diagram assets</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-2">
            <p>
              The diagram is hand-authored SVG so the team can update it without redrawing.
              Edit <code>src/assets/helpdesk-architecture-phase3.svg</code> directly, then re-export the PNG:
            </p>
            <pre className="bg-muted rounded-md p-3 overflow-x-auto text-xs">
{`# requires Node + sharp (one-off): bun add -d sharp
bun -e "require('sharp')('src/assets/helpdesk-architecture-phase3.svg',{density:200})
  .resize(2000).png().toFile('src/assets/helpdesk-architecture-phase3.png')"`}
            </pre>
            <p>
              Copies for the in-app download buttons live in <code>public/</code> — re-copy them after each edit.
            </p>
          </CardContent>
        </Card>
      </section>
    </main>
  );
}
